import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Briefcase, Building2, ArrowRight, Sparkles, Target,
} from 'lucide-react';

/* ----------------------------------------------------------------------------
 * Public home — a SHORT routing page for the two-sided platform. Its only job
 * is to send the two audiences to the right place: candidates (free AI job
 * agent) and recruiters (paid intent-scored talent pool). Blue brand, editorial
 * serif headings, hairline borders, subtle motion. Matches Product.js / Pool.js.
 * -------------------------------------------------------------------------- */

const BLUE = '#2563EB';

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
};

const SIDES = [
  {
    icon: Briefcase,
    eyebrow: 'For candidates',
    title: "I'm looking for a job",
    desc: 'A free AI agent that applies you to the jobs you can actually get interviews for — no spraying, no spam.',
    to: '/for-candidates',
    cta: "I'm looking for a job",
  },
  {
    icon: Building2,
    eyebrow: 'For recruiters',
    title: "I'm hiring",
    desc: 'Skip the application flood. Start with a pool of motivated, intent-scored tech candidates who actually want your role.',
    to: '/for-recruiters',
    cta: "I'm hiring",
  },
];

function Home() {
  return (
    <div className="bg-[#F8FAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-white to-[#F8FAFC] dark:from-gray-950 dark:to-gray-950">
        <div className="mx-auto max-w-5xl px-6 pt-20 pb-12 text-center lg:px-8 lg:pt-28 lg:pb-16">
          <motion.div {...reveal} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.05em] text-gray-500">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: BLUE }} />
            The two-sided hiring platform
          </motion.div>
          <motion.h1 {...reveal} transition={{ duration: 0.55, delay: 0.05 }}
            className="mt-6 font-serif text-5xl font-semibold leading-[1.05] tracking-tight text-gray-900 dark:text-white sm:text-6xl">
            Hiring, warmed up.
          </motion.h1>
          <motion.p {...reveal} transition={{ duration: 0.55, delay: 0.1 }}
            className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
            AutoHire connects motivated tech candidates with the startups hiring them — and skips the application flood.
          </motion.p>
        </div>

        {/* ===== TWO BIG ROUTING CARDS ===== */}
        <div className="mx-auto max-w-5xl px-6 pb-20 lg:px-8 lg:pb-24">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {SIDES.map((s, i) => (
              <motion.div key={s.to} {...reveal} transition={{ duration: 0.5, delay: i * 0.1 }}>
                <Link
                  to={s.to}
                  className="group flex h-full flex-col rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_48px_-28px_rgba(15,23,42,0.16)] transition-all hover:-translate-y-1 hover:border-gray-300 dark:hover:border-gray-700"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: '#EFF4FE', color: BLUE }}>
                    <s.icon size={22} strokeWidth={1.8} />
                  </div>
                  <div className="mt-6 text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: BLUE }}>{s.eyebrow}</div>
                  <h2 className="mt-2 font-serif text-2xl font-semibold tracking-tight text-gray-900 dark:text-white sm:text-3xl">{s.title}</h2>
                  <p className="mt-3 flex-grow text-[15px] leading-relaxed text-gray-600 dark:text-gray-300">{s.desc}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: BLUE }}>
                    {s.cta} <ArrowRight size={17} className="transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SHARED-INSIGHT STRIP ===== */}
      <section className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
        <div className="mx-auto max-w-3xl px-6 py-16 text-center lg:px-8">
          <motion.div {...reveal} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: BLUE }}>
            <Sparkles size={14} /> The problem with both sides
          </motion.div>
          <motion.p {...reveal} transition={{ duration: 0.55, delay: 0.06 }}
            className="mt-5 font-serif text-2xl font-medium leading-relaxed text-gray-900 dark:text-white sm:text-3xl">
            300+ applications per role. Most are noise.
          </motion.p>
          <motion.p {...reveal} transition={{ duration: 0.55, delay: 0.12 }}
            className="mt-4 text-lg leading-relaxed text-gray-600 dark:text-gray-300">
            We start with the people who actually want the job.
          </motion.p>
        </div>
      </section>

      {/* ===== TWO SHORT PROOF ROWS ===== */}
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-24">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Candidate proof row */}
          <motion.div {...reveal} transition={{ duration: 0.5 }}
            className="flex flex-col rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8">
            <div className="inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: '#EFF4FE', color: '#1D4FD8', border: '1px solid #BFDBFE' }}>
              <Briefcase size={14} strokeWidth={1.9} /> For candidates
            </div>
            <h3 className="mt-5 font-serif text-xl font-semibold text-gray-900 dark:text-white">
              Look motivated, not desperate.
            </h3>
            <p className="mt-3 flex-grow text-[15px] leading-relaxed text-gray-600 dark:text-gray-300">
              One AI agent that targets real fits and applies you to the roles you'll actually get interviews for. Free, and you control who sees your profile.
            </p>
            <Link to="/for-candidates" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: BLUE }}>
              See how it works <ArrowRight size={16} />
            </Link>
          </motion.div>

          {/* Recruiter proof row */}
          <motion.div {...reveal} transition={{ duration: 0.5, delay: 0.08 }}
            className="flex flex-col rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8">
            <div className="inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: '#EFF4FE', color: '#1D4FD8', border: '1px solid #BFDBFE' }}>
              <Target size={14} strokeWidth={1.9} /> For recruiters
            </div>
            <h3 className="mt-5 font-serif text-xl font-semibold text-gray-900 dark:text-white">
              Start with who's open, today.
            </h3>
            <p className="mt-3 flex-grow text-[15px] leading-relaxed text-gray-600 dark:text-gray-300">
              Skip the flood. Search a pool of motivated, intent-scored tech candidates, reach out warm, and run a lightweight pipeline.
            </p>
            <Link to="/for-recruiters" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: BLUE }}>
              See candidates for your role <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Home;
